import React, { useEffect, useState } from 'react';
import { Field, Modal, Spinner } from '../../components/ui';
import { fmtDate, money, PAYMENT_MODES } from '../../utils/helpers';
import '../../styles/money.css';

const basisText = (e, fnName) => (e.AmountType === 'PerPerson'
  ? `${money(e.Rate)} × ${e.persons}${e.PersonsSource === 'Headcount' ? ` (${fnName(e.PersonsFunctionId) || 'headcount'})` : ''}`
  : 'Fixed');

// Desktop Expenses: totals, the list grouped by category, payments per expense.
// Logic: useExpenses.
export default function ExpensesDesktop({ vm }) {
  const {
    canView, canAdd, canEdit, canDelete, loading, ownGuestsOnly, groups, totals, categories, functions, fnName, headcountOf, paidByOptions,
    q, setQ, catFilter, setCatFilter, onlyDue, setOnlyDue, openService,
    form, formTotal, formPersons, openAdd, openEdit, closeForm, setF, saveExpense, deleteExpense,
    payForm, openPayAdd, openPayEdit, closePay, setPF, savePayment, deletePayment, saving,
  } = vm;

  // Payments are always listed under their expense; this hides them on demand.
  const [hidePays, setHidePays] = useState(false);
  // Right-click menu: { x, y, kind: 'expense' | 'payment', e, p? } or null.
  const [menu, setMenu] = useState(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (ev) => { if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); close(); } };
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', onKey, true); };
  }, [menu]);
  const openMenu = (ev, kind, e, p) => {
    ev.preventDefault();
    ev.stopPropagation();
    setMenu({ x: Math.min(ev.clientX, window.innerWidth - 250), y: Math.min(ev.clientY, window.innerHeight - 170), kind, e, p });
  };
  const run = (fn) => () => { setMenu(null); fn(); };
  // Enter in a plain box of a dialog saves it (datalist boxes pick a suggestion instead).
  const enterTo = (fn) => (ev) => {
    if (ev.key !== 'Enter' || ev.defaultPrevented || saving) return;
    const t = ev.target;
    if (t.tagName !== 'INPUT' || t.type === 'checkbox' || t.getAttribute('list')) return;
    ev.preventDefault();
    fn();
  };

  if (!canView) {
    return (
      <div>
        <div className="page-head"><h1>Expenses</h1></div>
        <div className="card card-pad empty">You do not have access to expenses. Ask an Event Admin to grant it.</div>
      </div>
    );
  }
  const cols = 8;

  return (
    <div className="expenses-page">
      <div className="page-head">
        <h1>Expenses</h1>
        <div className="spacer" />
        {canAdd && <button className="btn primary" onClick={() => openAdd()}>+ Expense</button>}
      </div>

      {ownGuestsOnly && (
        <div className="muted" style={{ fontSize: 12.5, margin: '0 0 8px' }}>
          Per-person amounts count only the guests you can see (your own plus the public ones).
        </div>
      )}

      <div className="mn-tiles">
        <div className="mn-tile"><span>Total{totals.estimates ? ' (incl. estimates)' : ''}</span><strong>{money(totals.total)}</strong></div>
        <div className="mn-tile ok"><span>Paid</span><strong>{money(totals.paid)}</strong></div>
        <div className="mn-tile due"><span>Balance to pay</span><strong>{money(totals.balance)}</strong></div>
        <div className="mn-tile"><span>Expenses</span><strong>{totals.count}</strong><em>{totals.estimates} still estimates</em></div>
      </div>

      <div className="cards-filters">
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="expense-check"><input type="checkbox" checked={onlyDue} onChange={e => setOnlyDue(e.target.checked)} /> Only with balance due</label>
        <label className="expense-check"><input type="checkbox" checked={!hidePays} onChange={e => setHidePays(!e.target.checked)} /> Show payments</label>
        <span className="muted" style={{ fontSize: 12 }}>Right-click an expense or a payment for Edit / Delete</span>
        <input type="search" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {loading ? <div className="card card-pad"><Spinner /></div> : groups.length === 0 ? (
        <div className="card card-pad empty">No expenses yet.{canAdd && ' Add the hall booking, food, clothes, decoration…'}</div>
      ) : (
        <div className="card table-wrap">
          <table className="tbl compact expense-table">
            <thead>
              <tr>
                <th className="grow">Expense</th><th>For</th><th>Basis</th>
                <th className="mn-r">Total</th><th className="mn-r">Paid</th><th className="mn-r">Balance</th><th>Last paid</th><th></th>
              </tr>
            </thead>
            {groups.map(g => (
              <tbody key={g.category}>
                <tr className="expense-cat">
                  <td colSpan={3}><strong>{g.category}</strong> <span className="muted">· {g.rows.length}</span></td>
                  <td className="mn-r"><strong>{money(g.total)}</strong></td>
                  <td className="mn-r">{money(g.paid)}</td>
                  <td className="mn-r">{money(g.balance)}</td>
                  <td colSpan={2} style={{ textAlign: 'right' }}>
                    {canAdd && <button className="btn light sm" onClick={() => openAdd(g.category)}>+ Add</button>}
                  </td>
                </tr>
                {g.rows.map(e => (
                  <React.Fragment key={e.ExpenseId}>
                    <tr className={'row-link' + (menu && menu.e === e && menu.kind === 'expense' ? ' open' : '')}
                      onClick={() => (e.fromService ? openService(e) : (canEdit ? openEdit(e) : null))}
                      onContextMenu={(ev) => openMenu(ev, 'expense', e)}
                      title={e.fromService ? 'Open in Services · right-click for more' : 'Edit · right-click for more'}>
                      <td className="grow">
                        <strong>{e.Title}</strong>
                        {Number(e.IsEstimate) === 1 && <span className="chip warn" style={{ marginLeft: 6 }}>estimate</span>}
                        {e.fromService && <span className="chip" style={{ marginLeft: 6 }} title="Entered on the Services page">from Services</span>}
                        {(e.VendorName || e.Notes) && <div className="sub">{[e.VendorName, e.VendorMobile, e.Notes].filter(Boolean).join(' · ')}</div>}
                      </td>
                      <td>{e.FunctionId ? fnName(e.FunctionId) : 'Whole event'}</td>
                      <td>{basisText(e, fnName)}</td>
                      <td className="mn-r"><strong>{money(e.total)}</strong></td>
                      <td className="mn-r">{money(e.paid)}</td>
                      <td className={'mn-r' + (e.balance > 0 ? ' mn-due' : '')}>{money(e.balance)}</td>
                      <td>{e.LastPaymentDate ? fmtDate(e.LastPaymentDate) : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }} onClick={ev => ev.stopPropagation()}>
                        {canAdd && <button className="btn primary sm" onClick={() => openPayAdd(e)}>+ Pay</button>}
                        {e.fromService ? (
                          <button className="icon-btn" title="Change rate / plates / vendor on the Services page" onClick={() => openService(e)}>✎</button>
                        ) : (
                          <>
                            {canEdit && <button className="icon-btn" title="Edit" onClick={() => openEdit(e)}>✎</button>}
                            {canDelete && <button className="icon-btn danger" title="Delete" onClick={() => deleteExpense(e)}>🗑</button>}
                          </>
                        )}
                      </td>
                    </tr>
                    {!hidePays && e.payments.length > 0 && (
                      <tr className="expense-payments">
                        <td colSpan={cols}>
                          {(
                            <table className="tbl compact">
                              <thead><tr><th>Date</th><th className="mn-r">Amount</th><th>Paid by</th><th>Mode</th><th>Notes</th><th></th></tr></thead>
                              <tbody>
                                {e.payments.map(p => (
                                  <tr key={p.ExpensePaymentId} className={menu && menu.p === p ? 'ctx-on' : undefined}
                                    onContextMenu={(ev) => openMenu(ev, 'payment', e, p)}
                                    onDoubleClick={() => canEdit && openPayEdit(p)} title="Double-click to edit · right-click to edit or delete">
                                    <td>{fmtDate(p.PaymentDate)}</td>
                                    <td className="mn-r">{money(p.Amount)}</td>
                                    <td>{p.PaidBy || '—'}</td>
                                    <td>{p.PaymentMode || '—'}</td>
                                    <td>{p.Notes || ''}</td>
                                    <td style={{ textAlign: 'right' }}>
                                      {canEdit && <button className="icon-btn" title="Edit this payment" onClick={() => openPayEdit(p)}>✎</button>}
                                      {canDelete && <button className="icon-btn danger" title="Delete this payment" onClick={() => deletePayment(p)}>🗑</button>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      {menu && (
        <div className="ctx-menu" style={{ left: menu.x, top: menu.y }} onClick={(ev) => ev.stopPropagation()} onContextMenu={(ev) => ev.preventDefault()}>
          {menu.kind === 'payment' ? (
            <>
              <div className="ctx-title">Payment {money(menu.p.Amount)} · {fmtDate(menu.p.PaymentDate)}</div>
              <button type="button" disabled={!canEdit} onClick={run(() => openPayEdit(menu.p))}>✎ Edit payment</button>
              <button type="button" className="danger" disabled={!canDelete} onClick={run(() => deletePayment(menu.p))}>🗑 Delete payment</button>
            </>
          ) : (
            <>
              <div className="ctx-title">{menu.e.Title}</div>
              <button type="button" disabled={!canAdd} onClick={run(() => openPayAdd(menu.e))}>＋ Add payment</button>
              {menu.e.fromService ? (
                <button type="button" onClick={run(() => openService(menu.e))}>✎ Open in Services (rate, plates, vendor)</button>
              ) : (
                <>
                  <button type="button" disabled={!canEdit} onClick={run(() => openEdit(menu.e))}>✎ Edit expense</button>
                  <button type="button" className="danger" disabled={!canDelete} onClick={run(() => deleteExpense(menu.e))}>🗑 Delete expense</button>
                </>
              )}
            </>
          )}
        </div>
      )}

      <datalist id="expense-categories">{categories.map(c => <option key={c} value={c} />)}</datalist>
      <datalist id="expense-paidby">{paidByOptions.map(c => <option key={c} value={c} />)}</datalist>

      {form && (
        <Modal className="mn-exp-modal" title={form.ExpenseId ? 'Edit expense' : 'New expense'} onClose={closeForm}
          footer={<>
            <span className="mn-sum">
              {form.AmountType === 'PerPerson' && <>{formPersons} persons × {money(form.Rate)} = </>}
              <strong>{money(formTotal)}</strong>{form.IsEstimate ? ' (estimate)' : ''}
            </span>
            <button className="btn light" onClick={closeForm} disabled={saving}>Cancel</button>
            <button className="btn primary" onClick={saveExpense} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </>}>
          <div onKeyDown={enterTo(saveExpense)}>
            <div className="mn-line">
              <label className="field mn-w180">Category *
                <input autoFocus={!form.ExpenseId} list="expense-categories" value={form.Category} onChange={e => setF('Category', e.target.value)} placeholder="Hall / Venue" />
              </label>
              <div className="mn-grow">
                <Field label="Expense *" value={form.Title} onChange={(v) => setF('Title', v)} placeholder="e.g. Reception hall booking" />
              </div>
              <div className="mn-w200">
                <Field label="For function (blank = whole event)" type="select" value={form.FunctionId}
                  options={functions.map(f => ({ value: String(f.FunctionId), label: f.FunctionName }))}
                  onChange={(v) => setF('FunctionId', v)} />
              </div>
            </div>
            <div className="mn-line">
              <div className="mn-grow">
                <Field label="Vendor / shop" value={form.VendorName} onChange={(v) => setF('VendorName', v)} />
              </div>
              <label className="field mn-w150">Vendor mobile
                <input type="tel" inputMode="numeric" value={form.VendorMobile ?? ''} onChange={e => setF('VendorMobile', e.target.value)} />
              </label>
            </div>
            <div className="mn-line">
              <div className="mn-seg-cell">
                <span className="mn-seg-label">Amount is</span>
                <div className="mn-seg">
                  <button type="button" className={form.AmountType === 'Fixed' ? 'on' : ''} onClick={() => setF('AmountType', 'Fixed')}>A fixed amount</button>
                  <button type="button" className={form.AmountType === 'PerPerson' ? 'on' : ''} onClick={() => setF('AmountType', 'PerPerson')}>Rate × persons</button>
                </div>
              </div>
              {form.AmountType === 'Fixed' ? (
                <label className="field mn-w150 mn-amt">Total amount (₹)
                  <input inputMode="decimal" value={form.TotalAmount ?? ''} onChange={e => setF('TotalAmount', e.target.value)} />
                </label>
              ) : (
                <>
                  <label className="field mn-w120 mn-amt">Rate / person (₹)
                    <input inputMode="decimal" value={form.Rate ?? ''} onChange={e => setF('Rate', e.target.value)} />
                  </label>
                  <div className="mn-seg-cell">
                    <span className="mn-seg-label">Persons from</span>
                    <div className="mn-seg">
                      <button type="button" className={form.PersonsSource === 'Headcount' ? 'on' : ''} onClick={() => setF('PersonsSource', 'Headcount')}>Headcount</button>
                      <button type="button" className={form.PersonsSource !== 'Headcount' ? 'on' : ''} onClick={() => setF('PersonsSource', 'Manual')}>I type it</button>
                    </div>
                  </div>
                  {form.PersonsSource === 'Headcount' ? (
                    <div className="mn-w200">
                      <Field label="Headcount of" type="select" value={form.PersonsFunctionId}
                        options={functions.map(f => ({ value: String(f.FunctionId), label: `${f.FunctionName} (${headcountOf(f.FunctionId)})` }))}
                        onChange={(v) => setF('PersonsFunctionId', v)} />
                    </div>
                  ) : (
                    <label className="field mn-w100 mn-amt">Persons
                      <input inputMode="numeric" value={form.Persons ?? ''} onChange={e => setF('Persons', e.target.value)} />
                    </label>
                  )}
                </>
              )}
            </div>
            <Field label="Notes" type="textarea" rows={2} value={form.Notes} onChange={(v) => setF('Notes', v)} />
            <label className="expense-check">
              <input type="checkbox" checked={!!form.IsEstimate} onChange={e => setF('IsEstimate', e.target.checked ? 1 : 0)} />
              This is an estimate (final amount not known yet)
            </label>
          </div>
        </Modal>
      )}

      {payForm && (
        <Modal className="mn-pay-modal" title={payForm.ExpensePaymentId ? 'Edit payment' : 'Add payment'} onClose={closePay}
          footer={<>
            <span className="mn-sum">{Number(payForm.Amount) > 0 && <>Paying <strong>{money(payForm.Amount)}</strong></>}</span>
            <button className="btn light" onClick={closePay} disabled={saving}>Cancel</button>
            <button className="btn primary" onClick={savePayment} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </>}>
          <div onKeyDown={enterTo(savePayment)}>
            <div className="mn-line">
              <label className="field mn-w130 mn-amt">Amount (₹) *
                <input autoFocus inputMode="decimal" value={payForm.Amount ?? ''} onChange={e => setPF('Amount', e.target.value)} />
              </label>
              <div className="mn-w150">
                <Field label="Date *" type="date" value={payForm.PaymentDate} onChange={(v) => setPF('PaymentDate', v)} />
              </div>
              <div className="mn-grow">
                <Field label="Mode" type="select" value={payForm.PaymentMode} options={PAYMENT_MODES} onChange={(v) => setPF('PaymentMode', v)} />
              </div>
            </div>
            <div className="mn-line">
              <label className="field mn-w180">Paid by
                <input list="expense-paidby" value={payForm.PaidBy ?? ''} onChange={e => setPF('PaidBy', e.target.value)} />
              </label>
              <div className="mn-grow">
                <Field label="Notes" value={payForm.Notes} onChange={(v) => setPF('Notes', v)} placeholder="Receipt no., cheque no. …" />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
